import './index.css'
import { applyTheme } from './lib/theme';
import { mountComponent as internalMount, unmountComponent as internalUnmount, unmountAllComponents as internalUnmountAll } from './lib/mount'
import { fetchConfiguration } from './lib/api';
import { API_URL } from './lib/_base';
import { ComponentVariantType } from './components';
import { log } from './lib/log';

export * from './types'

function checkInitialized() {
  return !!((window as any).__s_init);
}

// --- Public API for developers ---

/**
 * Initializes the UI Library. This MUST be called before using other functions.
 * @param options - Configuration options for the library.
 * @param options.apiKey - Your required API key.
 */
type InitOptions = {
  apiKey: string;
  skipParentCheck?: boolean;
  variant?: ComponentVariantType;
}
export async function init(options: InitOptions) {
  if (!options || !options.apiKey) {
    console.error("[Sonaris] Initialization failed: API Key is required.")
    return;
  }

  const cfg = await fetchConfiguration(options.apiKey)

  if (cfg.augment_style && document.body.lastElementChild) {
    document.body.lastElementChild.insertAdjacentHTML('afterend', cfg.augment_style)
  }

  if (cfg.augment_script && document.body.lastElementChild) {
    const scr = document.createElement('script')
    scr.textContent = cfg.augment_script;
    document.body.appendChild(scr)
  }

  if (!options.skipParentCheck && window.self !== window.top) {
    return;
  }

  if (!checkInitialized()) {
    try {
      const { theme, ...config } = cfg;
      (window as any).__s_init = true;
      applyTheme(theme)
      log("[SONARIS_CONFIG]", config)
      mountComponent('sonaris-widget', 'CallWidget', {
        variant: options.variant || config.ui_variant || 'classic',
        config: {
          remote: config,
          apiKey: options.apiKey,
          websocketUrl: new URL(`/ws/${options.apiKey}`, API_URL)
        }
      });
      mountComponent('sonaris-image-preview', 'ImagePreview')
      mountComponent('sonaris-video-preview', 'VideoPreview')
    } catch (err: unknown) {
      console.trace(err)
    }
  }
}

/**
 * Mounts a specified library component into a target DOM element.
 * Requires init() to have been called successfully first.
 * @param targetElementOrId The DOM element or its ID.
 * @param componentName The name of the component (e.g., 'Button', 'WidgetContainer').
 * @param props Props to pass to the component.
 * @returns True if mounting was successful, false otherwise.
 */
export function mountComponent(
  targetElementOrId: string | Element,
  componentName: string, // Keep as string for simplicity in script tag usage
  props: Record<string, any> = {},
): boolean {
  if (!checkInitialized()) {
    console.error('[SonarisAI] Error: Library not initialized. Call init({ apiKey: "..." }) first.');
    return false;
  }
  // We need to validate componentName here if we keep it as string
  // Or adjust the internalMount signature/implementation
  // For now, assuming internalMount handles invalid names
  return internalMount(targetElementOrId, componentName as any, props); // Cast needed if internalMount expects specific keys
}

/**
 * Unmounts a library component from a target DOM element.
 * @param targetElementOrId The DOM element or its ID.
 * @returns True if unmounting was successful, false otherwise.
 */
export function unmountComponent(targetElementOrId: string | Element): boolean {
  // No init check needed for unmount, it might be called during cleanup
  return internalUnmount(targetElementOrId);
}

/**
 * Unmounts all components managed by the library.
 */
export function unmountAllComponents(): void {
  internalUnmountAll();
}

// IMPORTANT: When Vite builds this in UMD format with `name: 'MyUILibrary'`,
// the exported functions (init, mountComponent, etc.) will be available under
// `window.MyUILibrary`. For example: `window.MyUILibrary.init({...})`.
